import type { ScheduledEvent } from "./ScheduledEvent";

/**
 * Holds planned-but-not-yet-reserved events.
 *
 * The whole point of owning this queue (rather than letting Tone.Part or
 * Transport.schedule hold the events) is that Stop / Reset / Panic can discard
 * everything not yet handed to Web Audio by simply dropping array entries —
 * no Tone-internal state to unwind, and the count is directly observable.
 *
 * Invariants (docs/implementation-plan.md 9.12):
 *  - an event is never returned twice from takeDue()
 *  - prune() keeps the array bounded
 *  - discardUndispatched() leaves pendingCount === 0
 */
export class PendingQueue {
  /** Sorted ascending by timeTicks. */
  private events: ScheduledEvent[] = [];

  push(incoming: readonly ScheduledEvent[]): void {
    if (incoming.length === 0) return;
    for (const event of incoming) {
      this.insertSorted(event);
    }
  }

  private insertSorted(event: ScheduledEvent): void {
    // Plans arrive in time order, so scanning from the end is O(1) in practice.
    let i = this.events.length - 1;
    while (i >= 0) {
      const at = this.events[i];
      if (at !== undefined && at.timeTicks <= event.timeTicks) break;
      i -= 1;
    }
    this.events.splice(i + 1, 0, event);
  }

  /**
   * Returns undispatched events due within [fromTicks, fromTicks + windowTicks]
   * and marks them dispatched. Events already marked are never returned again.
   */
  takeDue(fromTicks: number, windowTicks: number): ScheduledEvent[] {
    const limit = fromTicks + windowTicks;
    const due: ScheduledEvent[] = [];
    for (const event of this.events) {
      if (event.timeTicks > limit) break; // sorted: nothing later can be due
      if (!event.dispatched) {
        event.dispatched = true;
        due.push(event);
      }
    }
    return due;
  }

  /** Stop / Panic: drop everything not yet reserved. Returns how many were dropped. */
  discardUndispatched(): number {
    const before = this.events.length;
    this.events = this.events.filter((event) => event.dispatched);
    return before - this.events.length;
  }

  /** Reset: drop everything, including the already-reserved bookkeeping. */
  discardAll(): void {
    this.events = [];
  }

  /** Removes dispatched events that are already in the past, keeping the array bounded. */
  prune(nowTicks: number): void {
    this.events = this.events.filter((event) => !event.dispatched || event.timeTicks >= nowTicks);
  }

  get pendingCount(): number {
    let count = 0;
    for (const event of this.events) {
      if (!event.dispatched) count += 1;
    }
    return count;
  }

  get size(): number {
    return this.events.length;
  }

  earliestUndispatchedTicks(): number | null {
    for (const event of this.events) {
      if (!event.dispatched) return event.timeTicks;
    }
    return null;
  }
}
